import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  CameraAlt as PhotoIcon,
  Straighten as MeasurementIcon,
  Notes as NoteIcon,
  ListAlt as SelectionIcon,
  HelpOutline as QuestionIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Storage } from 'aws-amplify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import DisplayMedia from '../../../components/DisplayMedia';

const TASK_LIST_API_URL =
  process.env.REACT_APP_TASK_LIST_API_URL ||
  'https://9n1cmips75.execute-api.us-west-2.amazonaws.com/staging';

interface Measurement {
  key: string;
  value: number | null;
  unit: string;
  createdAt: string | null;
}

interface Utterance {
  text: string;
  actionType: string;
  confidence: number | null;
  createdAt: string | null;
}

interface TaskListItem {
  id: string;
  name: string;
  description: string | null;
  taskType: string;
  itemKey: string;
  isCompleted: boolean;
  isSkipped: boolean;
  sortOrder: number;
  unitOfMeasure: string;
  userTranscript: string | null;
  aiTranscript: string | null;
  measurements: Measurement[];
  utterances: Utterance[];
}

interface TaskList {
  id: string;
  dealId: string;
  templateName: string;
  templateDescription: string | null;
  isCompleted: boolean;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  items: TaskListItem[];
}

interface DealFile {
  id: string;
  url?: string;
  fileName?: string;
  type?: string;
  tags?: string[];
  description?: string;
  isDeleted?: boolean;
}

interface DealInspectionQAProps {
  dealID: string;
  dealFiles?: DealFile[];
}

type StatusFilter = 'all' | 'completed' | 'incomplete';

const TASK_TYPE_ICONS: Record<string, React.ReactNode> = {
  photo: <PhotoIcon fontSize="small" />,
  measurement: <MeasurementIcon fontSize="small" />,
  note: <NoteIcon fontSize="small" />,
  selection: <SelectionIcon fontSize="small" />,
  question: <QuestionIcon fontSize="small" />,
};

const TASK_TYPE_LABELS: Record<string, string> = {
  photo: 'Photo',
  question: 'Question',
  measurement: 'Measurement',
  note: 'Note',
  selection: 'Selection',
};

const DealInspectionQA: React.FC<DealInspectionQAProps> = ({
  dealID,
  dealFiles = [],
}) => {
  const { theme } = useTheme();
  const [taskLists, setTaskLists] = useState<TaskList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [downloadingTaskId, setDownloadingTaskId] = useState<string | null>(
    null
  );

  const fetchTaskLists = useCallback(async () => {
    if (!dealID) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${TASK_LIST_API_URL}/task-list?dealId=${encodeURIComponent(dealID)}`
      );
      const json = await res.json();
      if (json.success && json.data?.taskLists) {
        setTaskLists(json.data.taskLists);
      } else {
        setTaskLists([]);
      }
    } catch (err: any) {
      console.error('Error fetching inspection Q&A:', err);
      setError(err.message || 'Failed to load inspection data');
    } finally {
      setIsLoading(false);
    }
  }, [dealID]);

  useEffect(() => {
    fetchTaskLists();
  }, [fetchTaskLists]);

  const photosByTag = useMemo(() => {
    const map: Record<string, DealFile[]> = {};
    const validFiles = dealFiles.filter(
      (f) =>
        f &&
        !f.isDeleted &&
        f.type?.startsWith('image/') &&
        (f.url || f.fileName || f.id)
    );
    for (const file of validFiles) {
      if (Array.isArray(file.tags)) {
        for (const tag of file.tags) {
          if (!map[tag]) map[tag] = [];
          map[tag].push(file);
        }
      }
    }
    return map;
  }, [dealFiles]);

  const allTaskTypes = useMemo(() => {
    const types = new Set<string>();
    for (const tl of taskLists) {
      for (const item of tl.items) {
        types.add(item.taskType);
      }
    }
    return Array.from(types);
  }, [taskLists]);

  const filterItems = useCallback(
    (items: TaskListItem[]) => {
      let filtered = items;
      if (statusFilter === 'completed') {
        filtered = filtered.filter((i) => i.isCompleted);
      } else if (statusFilter === 'incomplete') {
        filtered = filtered.filter((i) => !i.isCompleted);
      }
      if (selectedTaskType) {
        filtered = filtered.filter((i) => i.taskType === selectedTaskType);
      }
      return filtered;
    },
    [statusFilter, selectedTaskType]
  );

  const formatMeasurement = (m: Measurement) => {
    if (m.value == null) return '—';
    const unitLabel = m.unit?.replace(/_/g, ' ') || '';
    return `${m.value} ${unitLabel}`.trim();
  };

  const getPhotosForTask = (item: TaskListItem): DealFile[] => {
    return photosByTag[item.name] || photosByTag[item.itemKey] || [];
  };

  const handleDownloadTaskPhotos = async (item: TaskListItem) => {
    const photos = getPhotosForTask(item);
    if (photos.length === 0) return;
    setDownloadingTaskId(item.id);
    try {
      const zip = new JSZip();
      let count = 0;
      for (const file of photos) {
        try {
          const key = file.url || file.fileName || file.id;
          const signedUrl = await Storage.get(key);
          const res = await fetch(signedUrl);
          const blob = await res.blob();
          const name = key.split('/').pop() || `photo-${count}`;
          zip.file(name, blob);
          count++;
        } catch {}
      }
      if (count > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const safeName = (item.name || item.itemKey)
          .replace(/[^a-zA-Z0-9]/g, '_')
          .substring(0, 30);
        saveAs(zipBlob, `${safeName}_photos.zip`);
      }
    } catch (err) {
      console.error('Error downloading task photos:', err);
    } finally {
      setDownloadingTaskId(null);
    }
  };

  const renderTaskPhotos = (item: TaskListItem) => {
    if (item.taskType !== 'photo') return null;
    const photos = getPhotosForTask(item);
    if (photos.length === 0) return null;

    const isDownloading = downloadingTaskId === item.id;
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {photos.map((file) => {
            const imageName = file.url || file.fileName || file.id;
            return (
              <Box
                key={file.id}
                onClick={() => setLightboxImage(imageName)}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: `1px solid ${theme.stroke}`,
                  '&:hover': { opacity: 0.85 },
                }}
              >
                <DisplayMedia
                  imageName={imageName}
                  imageType="image"
                  height={80}
                  width={80}
                  borderRadius={6}
                  objectFit="cover"
                />
              </Box>
            );
          })}
        </Box>
        <Button
          onClick={() => handleDownloadTaskPhotos(item)}
          disabled={isDownloading}
          size="small"
          startIcon={
            isDownloading ? (
              <CircularProgress size={14} sx={{ color: theme.primary }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            mt: 1,
            color: theme.primary,
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 500,
            px: 1,
            '&:hover': { backgroundColor: `${theme.primary}10` },
          }}
        >
          {isDownloading
            ? 'Downloading...'
            : `Download Photos (${photos.length})`}
        </Button>
      </Box>
    );
  };

  const renderTranscript = (text: string, variant: 'user' | 'ai' = 'user') => (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        backgroundColor:
          variant === 'user' ? `${theme.primary}08` : `${theme.textLight}08`,
        borderLeft: `3px solid ${
          variant === 'user' ? theme.primary : theme.textLight
        }`,
        borderRadius: '0 6px 6px 0',
      }}
    >
      <Typography sx={{ color: theme.text, fontSize: 14, lineHeight: 1.5 }}>
        {text}
      </Typography>
    </Box>
  );

  const renderAnswer = (item: TaskListItem) => {
    const hasUserTranscript = !!item.userTranscript;
    const hasAiTranscript = !!item.aiTranscript;
    const hasUtterances = item.utterances?.length > 0;
    const hasMeasurements = item.measurements?.length > 0;

    if (item.isSkipped) {
      return (
        <Chip
          label="Skipped"
          size="small"
          sx={{
            mt: 0.5,
            backgroundColor: `${theme.textLight}15`,
            color: theme.textLight,
            fontWeight: 500,
            fontSize: 12,
          }}
        />
      );
    }

    if (item.taskType === 'question') {
      if (hasUserTranscript) {
        return renderTranscript(item.userTranscript!, 'user');
      }
      if (hasUtterances) {
        return (
          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              backgroundColor: `${theme.primary}08`,
              borderLeft: `3px solid ${theme.primary}`,
              borderRadius: '0 6px 6px 0',
            }}
          >
            {item.utterances.map((u, i) => (
              <Typography
                key={i}
                sx={{ color: theme.text, fontSize: 14, lineHeight: 1.5 }}
              >
                {u.text}
              </Typography>
            ))}
          </Box>
        );
      }
    }

    if (hasUserTranscript) {
      return renderTranscript(item.userTranscript!, 'user');
    }

    if (hasMeasurements) {
      return item.measurements.map((m, i) => (
        <Typography key={i} sx={{ color: theme.text, fontSize: 14, mt: 0.5 }}>
          {formatMeasurement(m)}
        </Typography>
      ));
    }

    if (hasUtterances) {
      return (
        <Box sx={{ mt: 0.5 }}>
          {item.utterances.map((u, i) => (
            <Typography key={i} sx={{ color: theme.text, fontSize: 14 }}>
              {u.text}
            </Typography>
          ))}
        </Box>
      );
    }

    if (hasAiTranscript) {
      return renderTranscript(item.aiTranscript!, 'ai');
    }

    if (item.isCompleted) {
      return (
        <Chip
          label="Completed"
          size="small"
          sx={{
            mt: 0.5,
            backgroundColor: `${theme.primary}20`,
            color: theme.primary,
            fontWeight: 500,
            fontSize: 12,
          }}
        />
      );
    }

    return (
      <Typography
        sx={{
          color: theme.textLight,
          fontSize: 14,
          mt: 0.5,
          fontStyle: 'italic',
        }}
      >
        No response yet
      </Typography>
    );
  };

  const renderTaskItem = (item: TaskListItem) => (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.5,
        borderBottom: `1px solid ${theme.stroke}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          mt: 0.25,
          color: item.isSkipped
            ? theme.textLight
            : item.isCompleted
            ? theme.primary
            : theme.textLight,
          opacity: item.isSkipped ? 0.5 : 1,
        }}
      >
        {item.isCompleted ? (
          <CheckCircleIcon fontSize="small" />
        ) : (
          <UncheckedIcon fontSize="small" />
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>
            {item.name || item.itemKey}
          </Typography>
          <Box
            sx={{
              color: theme.textLight,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {TASK_TYPE_ICONS[item.taskType] || null}
          </Box>
        </Box>

        {item.description && (
          <Typography sx={{ color: theme.textLight, fontSize: 12, mt: 0.25 }}>
            {item.description}
          </Typography>
        )}

        {renderAnswer(item)}
        {renderTaskPhotos(item)}
      </Box>
    </Box>
  );

  const renderFilters = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          sx={{ color: theme.textLight, fontSize: 12, fontWeight: 600 }}
        >
          Status:
        </Typography>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, val) => val && setStatusFilter(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontSize: 12,
              px: 1.5,
              py: 0.25,
              color: theme.textLight,
              borderColor: theme.stroke,
              '&.Mui-selected': {
                backgroundColor: `${theme.primary}15`,
                color: theme.primary,
                borderColor: theme.primary,
                '&:hover': { backgroundColor: `${theme.primary}25` },
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="incomplete">Incomplete</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {allTaskTypes.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{ color: theme.textLight, fontSize: 12, fontWeight: 600 }}
          >
            Type:
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setSelectedTaskType(null)}
            sx={{
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              backgroundColor: !selectedTaskType
                ? `${theme.primary}20`
                : `${theme.textLight}10`,
              color: !selectedTaskType ? theme.primary : theme.textLight,
              border: !selectedTaskType
                ? `1px solid ${theme.primary}`
                : `1px solid ${theme.stroke}`,
            }}
          />
          {allTaskTypes.map((type) => (
            <Chip
              key={type}
              icon={
                <Box sx={{ display: 'flex', ml: 0.5, color: 'inherit' }}>
                  {TASK_TYPE_ICONS[type]}
                </Box>
              }
              label={TASK_TYPE_LABELS[type] || type}
              size="small"
              onClick={() =>
                setSelectedTaskType(selectedTaskType === type ? null : type)
              }
              sx={{
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor:
                  selectedTaskType === type
                    ? `${theme.primary}20`
                    : `${theme.textLight}10`,
                color:
                  selectedTaskType === type ? theme.primary : theme.textLight,
                border:
                  selectedTaskType === type
                    ? `1px solid ${theme.primary}`
                    : `1px solid ${theme.stroke}`,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  const renderTaskList = (taskList: TaskList, index: number) => {
    const filteredItems = filterItems(taskList.items);
    const completedCount = taskList.items.filter((i) => i.isCompleted).length;
    const totalCount = taskList.items.length;

    return (
      <Accordion
        key={taskList.id}
        defaultExpanded={index === 0}
        sx={{
          backgroundColor: theme.background2,
          border: `1px solid ${theme.stroke}`,
          borderRadius: '8px !important',
          mb: 1.5,
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: theme.textLight }} />}
          sx={{ px: 2 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{ color: theme.text, fontWeight: 600, fontSize: 15 }}
            >
              {taskList.templateName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={taskList.isCompleted ? 'Completed' : 'In Progress'}
                size="small"
                sx={{
                  backgroundColor: taskList.isCompleted
                    ? `${theme.primary}20`
                    : `${theme.textLight}20`,
                  color: taskList.isCompleted ? theme.primary : theme.textLight,
                  fontWeight: 500,
                  fontSize: 11,
                  height: 22,
                }}
              />
              <Typography sx={{ color: theme.textLight, fontSize: 12 }}>
                {completedCount}/{totalCount} tasks
              </Typography>
              {taskList.createdAt && (
                <Typography sx={{ color: theme.textLight, fontSize: 12 }}>
                  {new Date(taskList.createdAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0 }}>
          {filteredItems.length > 0 ? (
            filteredItems.map(renderTaskItem)
          ) : (
            <Typography
              sx={{
                color: theme.textLight,
                fontSize: 13,
                py: 2,
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              No tasks match the selected filters
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderLightbox = () => (
    <Dialog
      open={!!lightboxImage}
      onClose={() => setLightboxImage(null)}
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={() => setLightboxImage(null)}
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        {lightboxImage && (
          <DisplayMedia
            imageName={lightboxImage}
            imageType="image"
            height={600}
            width={800}
            borderRadius={8}
            objectFit="contain"
          />
        )}
      </DialogContent>
    </Dialog>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        py: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography sx={{ color: theme.textLight, fontSize: 14 }}>
        No inspection data available for this deal.
      </Typography>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: theme.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: '#e53935', fontSize: 14 }}>{error}</Typography>
      </Box>
    );
  }

  if (taskLists.length === 0) return renderEmpty();

  return (
    <Box sx={{ py: 1 }}>
      {renderFilters()}
      {taskLists.map((tl, i) => renderTaskList(tl, i))}
      {renderLightbox()}
    </Box>
  );
};

export default DealInspectionQA;
